// src/forms/AddHymnForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import toast from "react-hot-toast";

// local helpers
const filter = createFilterOptions();

// --- small helpers (keep behavior as-is, just safe) ---
const slugify = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const notifySuccess = (msg) => {
  try { toast.success(msg); } catch { alert(msg); }
};
const notifyError = (msg) => {
  try { toast.error(msg); } catch { alert(msg); }
};
const notifyInfo = (msg) => {
  try { toast(msg); } catch {}
};

// -----------------------
// Quick Add Dialog (re-used for author/key/book)
// -- with de-dup protection
// -----------------------
function QuickAddDialog({ open, onClose, kind, onCreated }) {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");

  const handleSave = async () => {
    try {
      let col = "";
      let payload = { createdAt: serverTimestamp() };

      if (kind === "author") {
        col = "hymn_authors";
        payload = { name: name.trim(), name_slug: slugify(name), ...payload };
      } else if (kind === "key") {
        col = "hymn_keys";
        payload = { label: (label || name).trim(), value: name.trim(), ...payload };
      } else if (kind === "book") {
        col = "hymnbooks";
        payload = { name: name.trim(), name_slug: slugify(name), ...payload };
      }

      // Basic input guard
      if (!name.trim() && kind !== "key") {
        notifyError("Please enter a name");
        return;
      }

      // Prevent duplicates by name_slug (author/book) or label (key)
      if (kind === "author" || kind === "book") {
        const slug = slugify(name);
        const qy = query(collection(db, col), where(kind === "author" ? "name_slug" : "name_slug", "==", slug), limit(1));
        const snap = await getDocs(qy);
        if (!snap.empty) {
          // already exists -> reuse
          const existing = snap.docs[0];
          onCreated({ id: existing.id, ...existing.data() });
          notifyInfo("Already existed — selected it.");
          setName("");
          setLabel("");
          onClose();
          return;
        }
      }
      if (kind === "key") {
        const qy = query(collection(db, col), where("label", "==", (label || name).trim()), limit(1));
        const snap = await getDocs(qy);
        if (!snap.empty) {
          const existing = snap.docs[0];
          onCreated({ id: existing.id, ...existing.data() });
          notifyInfo("Already existed — selected it.");
          setName("");
          setLabel("");
          onClose();
          return;
        }
      }

      // create new
      const ref = await addDoc(collection(db, col), payload);
      onCreated({ id: ref.id, ...payload });
      setName("");
      setLabel("");
      onClose();
      notifySuccess(`${kind} added successfully`);
    } catch (e) {
      console.error("QuickAdd error", e);
      notifyError("Save failed: " + e.message);
    }
  };

  const title =
    kind === "author"
      ? "Add New Author"
      : kind === "key"
      ? "Add New Key"
      : "Add New Hymnbook";

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {kind === "key" ? (
            <>
              <TextField
                label="Key label (shown) e.g., E Flat"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                label="Key value (short) e.g., Eb"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
            </>
          ) : (
            <TextField
              label={kind === "book" ? "Hymnbook name" : "Author name"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// -----------------------
// Add Hymn Form
// -----------------------
export default function AddHymnForm({ onAfterCreate }) {
  const [title, setTitle] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [masterNumber, setMasterNumber] = useState("");

  // data models
  const [authors, setAuthors] = useState([]);
  const [keys, setKeys] = useState([]);
  const [books, setBooks] = useState([]);

  // selections
  const [selectedAuthorId, setSelectedAuthorId] = useState("");
  const [selectedKeyId, setSelectedKeyId] = useState("");
  const [selectedBooks, setSelectedBooks] = useState([]); // [{id,name,hymn_number}]

  // ui
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // quick add
  const [qaOpen, setQaOpen] = useState(false);
  const [qaKind, setQaKind] = useState(null);

  const titleRef = useRef(null);

  // --- load dropdown data, and suggest next master number
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [aSnap, kSnap, bSnap] = await Promise.all([
          getDocs(collection(db, "hymn_authors")),
          getDocs(collection(db, "hymn_keys")),
          getDocs(collection(db, "hymnbooks")),
        ]);

        // De-duplicate authors/keys by id or slug just in case
        const authorsRaw = aSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const uniqueAuthors = [];
        const seenAuthor = new Set();
        for (const a of authorsRaw) {
          const key = a.id + "::" + (a.name_slug || "");
          if (seenAuthor.has(key)) continue;
          seenAuthor.add(key);
          uniqueAuthors.push(a);
        }

        const keysRaw = kSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const uniqueKeys = [];
        const seenKey = new Set();
        for (const k of keysRaw) {
          const key = k.id + "::" + (k.label || "");
          if (seenKey.has(key)) continue;
          seenKey.add(key);
          uniqueKeys.push(k);
        }

        setAuthors(uniqueAuthors);
        setKeys(uniqueKeys);
        setBooks(bSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        // suggest next # by scanning
        const hymnsRef = collection(db, "hymns");
        const qy = query(hymnsRef, orderBy("master_number", "desc"), limit(1));
        const snap = await getDocs(qy);
        if (snap.empty) setMasterNumber(1);
        else setMasterNumber((snap.docs[0].data().master_number || 0) + 1);
      } catch (e) {
        console.error(e);
        notifyError("Failed to load dropdown data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // for faster lookup
  const booksById = useMemo(() => {
    const m = new Map();
    books.forEach((b) => m.set(b.id, b));
    return m;
  }, [books]);

  // auto-suggest hymn numbers per selected book
  useEffect(() => {
    (async () => {
      const clones = [...selectedBooks];
      let changed = false;
      for (let i = 0; i < clones.length; i++) {
        const b = clones[i];
        if (!b.hymn_number && b.hymn_number !== 0) {
          try {
            // next number inside that book
            const entriesRef = collection(db, "hymnbook_entries");
            const qy = query(
              entriesRef,
              where("book_id", "==", b.id),
              orderBy("hymn_number", "desc"),
              limit(1)
            );
            const snap = await getDocs(qy);
            const next = snap.empty ? 1 : (snap.docs[0].data().hymn_number || 0) + 1;
            clones[i] = { ...b, hymn_number: next };
            changed = true;
          } catch (e) {
            console.warn("Suggest number failed for", b.id, e);
          }
        }
      }
      if (changed) setSelectedBooks(clones);
    })();
  }, [selectedBooks]);

  // --- handlers
  const handleSelectBooks = (ids) => {
    const preserved = selectedBooks.filter((sb) => ids.includes(sb.id));
    const toAdd = ids
      .filter((id) => !preserved.some((p) => p.id === id))
      .map((id) => ({
        id,
        name: booksById.get(id)?.name || id,
        hymn_number: "",
      }));
    setSelectedBooks([...preserved, ...toAdd]);
  };

  const handleBookNumberChange = (bookId, val) => {
    setSelectedBooks((prev) =>
      prev.map((b) =>
        b.id === bookId ? { ...b, hymn_number: Number(val) || "" } : b
      )
    );
  };

  const quickAdd = (kind) => {
    setQaKind(kind);
    setQaOpen(true);
  };

  const onQuickCreated = (model) => {
    // Deduplicate when adding (fixes “author added twice”)
    if (qaKind === "author") {
      setAuthors((prev) => {
        const next = [ { id: model.id, ...model }, ...prev ];
        const seen = new Set();
        const deduped = [];
        for (const a of next) {
          const k = a.id + "::" + (a.name_slug || "");
          if (seen.has(k)) continue;
          seen.add(k);
          deduped.push(a);
        }
        return deduped;
      });
      setSelectedAuthorId(model.id);
    } else if (qaKind === "key") {
      setKeys((prev) => {
        const next = [ { id: model.id, ...model }, ...prev ];
        const seen = new Set();
        const deduped = [];
        for (const k of next) {
          const key = k.id + "::" + (k.label || "");
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(k);
        }
        return deduped;
      });
      setSelectedKeyId(model.id);
    } else if (qaKind === "book") {
      setBooks((prev) => {
        const next = [ { id: model.id, ...model }, ...prev ];
        const seen = new Set();
        const deduped = [];
        for (const b of next) {
          const key = b.id + "::" + (b.name_slug || "");
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(b);
        }
        return deduped;
      });
      setSelectedBooks((prev) => [
        ...prev,
        { id: model.id, name: model.name || model.name_slug, hymn_number: "" },
      ]);
    }
  };

  // --- validation (same rules you use)
  const validate = () => {
    if (!title.trim()) return "Title is required.";
    if (!lyrics.trim()) return "Lyrics are required.";
    if (!masterNumber || Number(masterNumber) <= 0)
      return "Master Hymn # must be positive.";
    if (selectedBooks.length === 0) return "Select at least one hymnbook.";
    for (const b of selectedBooks) {
      if (!b.hymn_number || Number(b.hymn_number) <= 0) {
        return `Missing/invalid hymn number for: ${b.name}`;
      }
    }
    return "";
  };

  // --- Save
  const handleSave = async () => {
    const v = validate();
    if (v) return notifyError(v);

    try {
      setSaving(true);

      // ensure master # is unique
      const hymnsRef = collection(db, "hymns");
      const existsQ = query(hymnsRef, where("master_number", "==", Number(masterNumber)), limit(1));
      const existsSnap = await getDocs(existsQ);
      if (!existsSnap.empty) {
        return notifyError(`Hymn #${masterNumber} already exists. Pick another number.`);
      }

      // Structured lyrics now or later — storing raw only (your codebase parses on view)
      const hymnRef = await addDoc(collection(db, "hymns"), {
        master_number: Number(masterNumber),
        title: title.trim(),
        title_slug: slugify(title),
        lyrics: lyrics.trim(),
        author_ref: selectedAuthorId ? doc(db, "hymn_authors", selectedAuthorId) : null,
        key_ref: selectedKeyId ? doc(db, "hymn_keys", selectedKeyId) : null,
        author_name: authors.find((a) => a.id === selectedAuthorId)?.name || null,
        key_label: keys.find((k) => k.id === selectedKeyId)?.label || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // link entries
      const batch = writeBatch(db);
      selectedBooks.forEach((b) => {
        const linkId = `${b.id}__${hymnRef.id}`;
        const ref = doc(db, "hymnbook_entries", linkId);
        batch.set(ref, {
          book_id: b.id,
          book_name: b.name,
          hymn_id: hymnRef.id,
          hymn_title: title.trim(),
          hymn_number: Number(b.hymn_number),
          sort_order: Number(b.hymn_number),
          createdAt: serverTimestamp(),
        });
      });
      await batch.commit();

      notifySuccess(`Hymn created successfully (Hymn #${masterNumber})`);

      // Reset for next add
      setTitle("");
      setLyrics("");
      setSelectedAuthorId("");
      setSelectedKeyId("");
      setSelectedBooks([]);

      // suggest next
      const qy = query(collection(db, "hymns"), orderBy("master_number", "desc"), limit(1));
      const snap = await getDocs(qy);
      setMasterNumber(snap.empty ? 1 : (snap.docs[0].data().master_number || 0) + 1);

      setTimeout(() => titleRef.current?.focus(), 200);

      if (onAfterCreate) onAfterCreate();
    } catch (e) {
      console.error("[AddHymnForm] Save error:", e);
      notifyError("Save failed. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  // --- UI
  if (loading) {
    return (
      <Box sx={{ p: 6, display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={22} /> <Typography>Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        Add New Hymn
      </Typography>

      {/* Main block */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <TextField
          label="Master Hymn #"
          type="number"
          value={masterNumber}
          onChange={(e) => setMasterNumber(Number(e.target.value) || "")}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Title"
          value={title}
          inputRef={titleRef}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Author (searchable) + Quick Add */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={authors}
            value={authors.find((a) => a.id === selectedAuthorId) || null}
            onChange={(_, val) => setSelectedAuthorId(val?.id || "")}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(opt) => opt?.name || ""}
            filterOptions={filter}
            renderInput={(params) => (
              <TextField {...params} label="Written By (Author) — search or pick" />
            )}
          />
          <Tooltip title="Quick add author">
            <IconButton onClick={() => quickAdd("author")}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Key (searchable) + Quick Add */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={keys}
            value={keys.find((k) => k.id === selectedKeyId) || null}
            onChange={(_, val) => setSelectedKeyId(val?.id || "")}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(opt) => opt?.label || ""}
            filterOptions={filter}
            renderInput={(params) => (
              <TextField {...params} label="Song Key — search or pick" />
            )}
          />
          <Tooltip title="Quick add key">
            <IconButton onClick={() => quickAdd("key")}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <TextField
          label="Lyrics"
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          fullWidth
          multiline
          minRows={18}
          sx={{ mb: 1 }}
        />
      </Paper>

      {/* Hymnbooks block */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 8 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Link to Hymnbooks
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Quick add hymnbook">
              <IconButton onClick={() => quickAdd("book")}>
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Auto-fill empty numbers">
              <IconButton onClick={() => setSelectedBooks((prev) => [...prev])}>
                <AutoFixHighIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <TextField
          select
          SelectProps={{
            multiple: true,
            renderValue: (vals) => (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {vals.map((id) => (
                  <Chip key={id} label={booksById.get(id)?.name || id} />
                ))}
              </Box>
            ),
          }}
          label="Select Hymnbooks"
          fullWidth
          value={selectedBooks.map((b) => b.id)}
          onChange={(e) => handleSelectBooks(e.target.value)}
          sx={{ mb: 2 }}
        >
          {books.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name || b.id}
            </MenuItem>
          ))}
        </TextField>

        {selectedBooks.length > 0 ? (
          <Box>
            {selectedBooks.map((b) => (
              <TextField
                key={b.id}
                label={`Hymn # in "${b.name}"`}
                type="number"
                value={b.hymn_number}
                onChange={(e) => handleBookNumberChange(b.id, e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ opacity: 0.7 }}>
            Pick at least one hymnbook; each needs its own number.
          </Typography>
        )}
      </Paper>

      {/* Sticky Save Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
          py: 2,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "center",
          gap: 2,
          zIndex: 10,
        }}
      >
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving…" : "💾 Save Hymn"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={async () => {
            setTitle("");
            setLyrics("");
            setSelectedAuthorId("");
            setSelectedKeyId("");
            setSelectedBooks([]);
            // re-suggest next number
            const qy = query(collection(db, "hymns"), orderBy("master_number", "desc"), limit(1));
            const snap = await getDocs(qy);
            setMasterNumber(snap.empty ? 1 : (snap.docs[0].data().master_number || 0) + 1);
            notifyInfo("Form reset");
            setTimeout(() => titleRef.current?.focus(), 150);
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Quick Add */}
      <QuickAddDialog
        open={qaOpen}
        onClose={() => setQaOpen(false)}
        kind={qaKind}
        onCreated={onQuickCreated}
      />
    </Box>
  );
}
